import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllOrders, getAllEngagements, getAllBrands } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import OrderDetailDrawer, { ORDER } from '../../components/orders/OrderDetailDrawer';
import CreateOrderForm from '../../components/orders/CreateOrderForm';
import { ORDER_STATUS, formatDate, formatINR, label, options } from '../../constants/garmentCodes';
import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';

export default function Orders() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const [selectedId, setSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  useCreateIntent(CREATE_INTENT.ORDER, () => setAddOpen(true));

  const orders = useSelector((state) => state.order.orders);
  const loading = useSelector((state) => state.order.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brands = useSelector((state) => state.brand.brands);

  useEffect(() => {
    dispatch(getAllEngagements());
    dispatch(getAllBrands());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllOrders({ engagementId, brandId: '', status: '' }));
  }, [dispatch, engagementId]);

  const stats = orders.reduce(
    (acc, o) => {
      acc.total += 1;
      if (o.status === ORDER.IN_PRODUCTION) acc.production += 1;
      if (o.status === ORDER.DISPATCHED) acc.dispatched += 1;
      if (o.status === ORDER.ESTIMATE_PENDING || o.status === ORDER.SUBMITTED) acc.estimate += 1;
      return acc;
    },
    { total: 0, production: 0, dispatched: 0, estimate: 0 },
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title="Orders" breadcrumbs={[{ workspace: true }, { label: 'Orders' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Total Orders</p>
            <p className="text-[22px] font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-100 p-4">
            <p className="text-[10px] text-blue-600 uppercase tracking-wide mb-1">In Production</p>
            <p className="text-[22px] font-bold text-blue-700">{stats.production}</p>
          </div>
          <div className="bg-teal-50 rounded-lg border border-teal-100 p-4">
            <p className="text-[10px] text-teal-600 uppercase tracking-wide mb-1">Dispatched</p>
            <p className="text-[22px] font-bold text-teal-700">{stats.dispatched}</p>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-100 p-4">
            <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Needs Estimate</p>
            <p className="text-[22px] font-bold text-amber-700">{stats.estimate}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="ml-auto flex items-center gap-3">
            {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
            <button onClick={() => setAddOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              + Create Order
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Orders"
            columns={[
              { id: 'orderNum', label: 'ORDER #', align: 'left' },
              { id: 'brand', label: 'BRAND', align: 'left' },
              { id: 'product', label: 'PRODUCT', align: 'left' },
              { id: 'qty', label: 'QTY', align: 'right' },
              { id: 'estimate', label: 'ESTIMATE', align: 'right' },
              { id: 'walletHold', label: 'WALLET HOLD', align: 'right' },
              { id: 'targetDelivery', label: 'TARGET DELIVERY', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={orders.map((order) => {
              const status = label(ORDER_STATUS, order.status);
              const open = () => !order.__optimistic && setSelectedId(order.id);
              return {
                id: order.id,
                rColumns: [
                  <div key="id" label={order.orderNumber || ''} onClick={open} className={`flex items-center w-full h-full px-2 py-1 font-mono text-[12px] text-[#1b3a6b] font-medium cursor-pointer ${order.__optimistic ? 'opacity-60' : ''}`}>{order.orderNumber || `#${order.id}`}</div>,
                  <div key="br" label={order.brandName || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] font-medium text-gray-800 cursor-pointer">{order.brandName || '—'}</div>,
                  <div key="prod" label={order.product || ''} onClick={open} className="flex flex-col justify-center items-start w-full h-full px-2 py-1 cursor-pointer">
                    <p className="text-[12px] text-gray-700">{order.product}</p>
                    <p className="text-[10px] text-gray-400">{[order.designName, order.fabricName].filter(Boolean).join(' • ')}</p>
                  </div>,
                  <div key="qty" label={String(order.totalQty ?? '')} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-700 cursor-pointer">{order.totalQty?.toLocaleString('en-IN') ?? '—'}</div>,
                  <div key="est" label={formatINR(order.totalEstimate)} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-gray-800 cursor-pointer">{order.totalEstimate ? formatINR(order.totalEstimate) : '—'}</div>,
                  <div key="hold" label={formatINR(order.walletHold)} onClick={open} className="flex items-center justify-end w-full h-full px-2 py-1 font-mono text-[12px] text-amber-600 cursor-pointer">{order.walletHold ? formatINR(order.walletHold) : '—'}</div>,
                  <div key="date" label={order.targetDelivery || ''} onClick={open} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600 cursor-pointer">{formatDate(order.targetDelivery)}</div>,
                  <div key="stat" label={status} onClick={open} className="flex items-center w-full h-full px-2 py-1 cursor-pointer"><Badge status={status} /></div>,
                  <div key="act" label="" className="flex items-center gap-2 w-full h-full px-2 py-1">
                    {!order.__optimistic && <button onClick={open} className="text-[11px] text-blue-600 font-medium hover:underline">Open</button>}
                  </div>,
                ],
              };
            })}
          />
          {!loading && orders.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No orders</p>}
        </div>
      </main>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Create Production Order" size="lg">
        <CreateOrderForm engagements={engagements} initialStatus={ORDER.SUBMITTED} onDone={() => setAddOpen(false)} />
      </Modal>

      <OrderDetailDrawer orderId={selectedId} role="manufacturer" onClose={() => setSelectedId(null)} />
    </div>
  );
}
